/**
 * Marketplace API client — thin wrapper over the shared api fetch
 * helper. Mirrors the contract in backend/src/handlers/marketplace.js:
 *
 *   listWorkflows({ category, author, sort, cursor, limit })
 *     → { items, nextCursor }
 *
 *   listTrending(limit)            → { items }
 *   listCategories()               → { categories: [{ name, count }] }
 *   getWorkflow(slug)              → { slug, name, description, ... }
 *   deployWorkflow(slug, projectId)
 *                                  → { alreadyInstalled, workflowSlug, projectId }
 *   rateWorkflow(slug, { rating, review })
 *                                  → { rating, review, ratingCount, ratingAvg }
 *   listRatings(slug, { cursor })  → { items, nextCursor }
 *
 * The marketplace API is BasePathMapped onto api-${stage}.zibby.app/marketplace/*,
 * so we hit `${API_BASE_URL}/marketplace/workflows...` and the shared
 * api client tacks on the JWT bearer header automatically.
 */

import api from './api';
import { isChatRuntime } from '../utils/chat-runtime';

const BASE = '/marketplace/workflows';

function qs(params) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export const Marketplace = {
  listWorkflows({ category, author, sort, cursor, limit } = {}) {
    return api.get(`${BASE}${qs({ category, author, sort, cursor, limit })}`);
  },

  listTrending(limit = 10) {
    return api.get(`${BASE}/trending${qs({ limit })}`);
  },

  listCategories() {
    return api.get(`${BASE}/categories`);
  },

  getWorkflow(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.get(`${BASE}/${encodeURIComponent(slug)}`);
  },

  /**
   * @param {string} slug
   * @param {string} projectId
   * @param {Object} [opts]
   * @param {number} [opts.warm]  0..5 warm Fargate tasks. Omit / undefined
   *   = inherit cold-start default. Server validates the range.
   * @param {string} [opts.workflowName]  Override workflowType (the URL
   *   slug under which the workflow lands in this project). Server
   *   validates shape + uniqueness; 409 on collision with a
   *   server-suggested alternative. ALSO the agent's display name —
   *   whatever the "Deploy as" box said, prefilled or typed. (The old
   *   `workflowNameIsDefault` opt-out is gone: a box whose value the
   *   server then discarded was a lie about what the control does.)
   * @param {boolean} [opts.force]  App-Store-style "Update" — re-copies
   *   the latest bundle into an existing install. Server-side preserves
   *   env vars, triggers, displayName, warm config. 404 if no prior
   *   install exists for (projectId, workflowName).
   * @param {string} [opts.aiAgent]  Per-workflow agent override
   *   ("claude" | "codex" | "gemini"). Omit / undefined =
   *   "project default" → server leaves the workflow row's aiAgent
   *   absent and runtime falls back to project.aiAgent.
   * @param {string} [opts.aiModel]  Per-workflow model override —
   *   ignored by the server unless `aiAgent` is also set. See
   *   packages/core/src/constants.js for the per-agent alias maps.
   */
  deployWorkflow(slug, projectId, opts = {}) {
    if (!slug || !projectId) return Promise.reject(new Error('slug and projectId are required'));
    const body = { projectId };
    if (typeof opts.warm === 'number') body.warm = opts.warm;
    if (typeof opts.workflowName === 'string' && opts.workflowName.trim()) {
      body.workflowName = opts.workflowName.trim();
    }
    if (opts.force === true) body.force = true;
    if (typeof opts.aiAgent === 'string' && opts.aiAgent) {
      body.aiAgent = opts.aiAgent;
    }
    if (typeof opts.aiModel === 'string' && opts.aiModel) {
      body.aiModel = opts.aiModel;
    }
    // Per-agent integration allowlist (least-privilege): the explicit set of
    // connected providers THIS agent may use. Sent only when non-empty; absent
    // = "all connected allowed" (backward-compatible). The server force-adds
    // the template's required members regardless of what we send.
    if (Array.isArray(opts.enabledIntegrations) && opts.enabledIntegrations.length > 0) {
      body.enabledIntegrations = opts.enabledIntegrations;
    }
    // BYOK: ad-hoc key collected inline when the project has none, + the
    // opt-in to also persist it as the project default. Without forwarding
    // these the backend never sees the key and 428s again.
    if (typeof opts.agentToken === 'string' && opts.agentToken) {
      body.agentToken = opts.agentToken;
    }
    if (opts.saveToProject === true) body.saveToProject = true;
    if (opts.defaultInput && typeof opts.defaultInput === 'object' && !Array.isArray(opts.defaultInput)) {
      body.defaultInput = opts.defaultInput;
    }
    return api.post(`${BASE}/${encodeURIComponent(slug)}/deploy`, body);
  },

  /**
   * GET /marketplace/workflows/{slug}/integration-status?projectId=<id>
   *
   * Returns { required, connected, missing, integrations } so the install
   * modal can render a ✓/✗ checklist and a "connect after deploy" CTA per
   * missing integration. Same shape as the per-workflow status endpoint.
   */
  getIntegrationStatus(slug, projectId) {
    if (!slug || !projectId) return Promise.reject(new Error('slug and projectId are required'));
    return api.get(`${BASE}/${encodeURIComponent(slug)}/integration-status${qs({ projectId })}`);
  },

  rateWorkflow(slug, { rating, review = '' } = {}) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.post(`${BASE}/${encodeURIComponent(slug)}/ratings`, { rating, review });
  },

  listRatings(slug, { cursor, limit } = {}) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.get(`${BASE}/${encodeURIComponent(slug)}/ratings${qs({ cursor, limit })}`);
  },

  /**
   * UX-only pre-check for the one-Copilot-per-account cap: does the signed-in
   * account ALREADY have a Copilot (chat-runtime agent) deployed in any of its
   * projects? Used to grey out the Deploy button when the active template is a
   * Copilot. The BACKEND deploy guard is the real enforcement — this is purely
   * so the button doesn't dead-end on a 409.
   *
   * Fans out GET /projects → GET /projects/:id/workflows per project and looks
   * for any deployed workflow whose marketplaceSlug is a chat template. Only
   * called when the user is about to deploy a Copilot template (rare), so the
   * fan-out is acceptable and not an always-on cost. Best-effort: any failure
   * resolves to `false` (don't block the UI on a check failing — the backend
   * still guards).
   *
   * @returns {Promise<boolean>}
   */
  async hasAccountCopilot() {
    try {
      const projRes = await api.get('/projects');
      const projects = Array.isArray(projRes?.projects) ? projRes.projects : [];
      if (projects.length === 0) return false;
      const lists = await Promise.all(
        projects.map((p) =>
          api
            .get(`/projects/${p.projectId}/workflows`)
            .then((res) => (Array.isArray(res) ? res : []))
            .catch(() => []),
        ),
      );
      return lists.some((wfs) => wfs.some((wf) => isChatRuntime(wf)));
    } catch {
      return false;
    }
  },
};

export default Marketplace;
